/* import __COLOCATED_TEMPLATE__ from './container.hbs'; */
/* RESPONSIBLE TEAM: team-messenger */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { action } from '@ember/object';
import EmberObject from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { registerDestructor } from '@ember/destroyable';
import { get, post, put } from 'embercom/lib/ajax';
import type IntlService from 'embercom/services/intl';
import type Router from '@ember/routing/router-service';
import type MessengerSettingsService from 'embercom/services/messenger-settings-service';
import type Store from '@ember-data/store';
//@ts-ignore no type declaration available
import PredicateGroup from 'predicates/models/predicate-group';
import type { SuggestionGroup } from './types';

interface Suggestion {
  id: number;
  text: string;
  display_priority: number;
}

interface GenerationJob {
  job_id: number;
  status: string;
  website_url: string;
  created_at: string;
  completed_at: string | null;
  error_message: string | null;
  error_code: string | null;
  draft_suggestion_groups?: SuggestionGroup[];
}

interface SalesMessengerConfig {
  id?: number;
  app_id?: string;
  brand_settings_id: number;
  glow_color: string | null;
  vertical_padding: number | null;
  audience_predicates: Array<Record<string, unknown>>;
  smart_suggestions_enabled: boolean;
  suggestions: Suggestion[];
  suggestion_groups: SuggestionGroup[];
  generation_job: GenerationJob | null;
  last_live_website_url: string | null;
}

const MIN_VERTICAL_PADDING = 20;

interface Args {
  model: $TSFixMe;
}

interface Signature {
  Element: HTMLElement;
  Args: Args;
}

export default class SalesMessengerContainer extends Component<Signature> {
  @service declare appService: $TSFixMe;
  @service declare notificationsService: $TSFixMe;
  @service declare intl: IntlService;
  @service declare router: Router;
  @service declare messengerSettingsService: MessengerSettingsService;
  @service declare store: Store;
  @service declare attributeService: $TSFixMe;

  @tracked isLoading = true;
  @tracked isSaving = false;
  @tracked config: SalesMessengerConfig | null = null;
  @tracked glowColor: string | null = null;
  @tracked verticalPadding: number | null = null;
  @tracked audiencePredicates: Array<Record<string, unknown>> = [];
  @tracked activeBrandSetting: $TSFixMe = null;
  @tracked selectedPreset = 'visitors-and-leads';
  @tracked audienceRuleset: $TSFixMe = null;
  @tracked openSectionId = 'audience';
  @tracked smartSuggestionsEnabled = false;
  @tracked suggestions: Suggestion[] = [];
  @tracked showAllSuggestions = false;
  @tracked suggestionGroups: SuggestionGroup[] = [];
  @tracked isGeneratingSuggestions = false;
  @tracked websiteUrl = '';
  @tracked isEditingWebsiteUrl = false;
  @tracked generationJob: GenerationJob | null = null;
  @tracked previousSuggestions: SuggestionGroup[] = [];
  @tracked previousWebsiteUrl = '';
  @tracked previousGenerationJob: GenerationJob | null = null;
  @tracked failedGenerationUrl = '';
  @tracked failedGenerationErrorMessage = '';
  @tracked failedGenerationErrorCode = '';
  @tracked isGenerationErrorDismissed = false;
  @tracked hasUnsavedSuggestions = false;
  private liveWebsiteUrl = '';
  private generationPollTimer: ReturnType<typeof setTimeout> | null = null;

  // Snapshot of last-saved values for dirty tracking
  private savedGlowColor: string | null = null;
  private savedVerticalPadding: number | null = null;
  private savedPredicates = '[]';
  private savedPreset = 'visitors-and-leads';
  private savedSmartSuggestionsEnabled = false;

  static PRESET_PREDICATES: Record<string, Array<Record<string, unknown>>> = {
    'visitors-and-leads': [
      {
        attribute: 'role',
        type: 'role',
        comparison: 'in',
        value: ['visitor_role', 'contact_role'],
      },
    ],
    everyone: [
      {
        attribute: 'role',
        type: 'role',
        comparison: 'in',
        value: ['contact_role', 'user_role', 'visitor_role'],
      },
    ],
    'no-one': [],
  };

  get canUseMultibrand(): boolean {
    return this.appService.app.canUseFeature('messenger_multibrand');
  }

  get canUseSmartSuggestions(): boolean {
    return this.appService.app.canUseFeature('team-messenger-sales-messenger-smart-suggestions');
  }

  get effectiveSmartSuggestionsEnabled(): boolean {
    return this.canUseSmartSuggestions && this.smartSuggestionsEnabled;
  }

  get previewSuggestions(): Suggestion[] {
    if (this.suggestionGroups.length === 0) {
      return this.suggestions;
    }
    let activeTexts = new Set(this.suggestionGroups.filter((g) => g.active).map((g) => g.text));
    return this.suggestions.filter((s) => activeTexts.has(s.text));
  }

  get currentLocale(): string {
    return this.intl.primaryLocale;
  }

  get showWebInstallationBanner(): boolean {
    return !this.appService.app.hasAnyInstalledAtDate && !this.appService.app.canUseStandalone;
  }

  get lookAndFeel() {
    return this.args.model.settings.lookAndFeel;
  }

  get brands() {
    return this.lookAndFeel.newBrands;
  }

  get brandSettings() {
    return this.lookAndFeel.brands;
  }

  get activeBrandSettingsId(): number | null {
    return this.activeBrandSetting?.id ?? null;
  }

  get isDirty(): boolean {
    if (!this.config) {
      return false;
    }
    if (this.glowColor !== this.savedGlowColor) {
      return true;
    }
    if (this.verticalPadding !== this.savedVerticalPadding) {
      return true;
    }
    // Toggle state changed (ON↔OFF)
    if (this.smartSuggestionsEnabled !== this.savedSmartSuggestionsEnabled) {
      return true;
    }
    if (this.selectedPreset !== this.savedPreset) {
      return true;
    }
    if (this.selectedPreset === 'specific' && this.audienceRuleset) {
      let allPredicates = this.serializeAudienceRuleset();
      return JSON.stringify(allPredicates) !== this.savedPredicates;
    }
    return JSON.stringify(this.audiencePredicates) !== this.savedPredicates;
  }

  get isSaveDisabled(): boolean {
    return !this.isDirty || this.isSaving;
  }

  get glowColorOrUndefined(): string | undefined {
    return this.glowColor ?? undefined;
  }

  get verticalPaddingString(): string | undefined {
    return this.verticalPadding != null ? String(this.verticalPadding) : undefined;
  }

  get rulesEditorConfig() {
    return {
      editorConfiguration: {
        workflowMatchingTargetingAttributeIdentifiers: this.getAttributeIdentifiers(),
        workflowMatchingTargetingAttributes: this.getAttributeGroups(),
        supportsUserTargeting: true,
        supportsConversationChannelTargeting: false,
        targetingAttributePickerLabel: this.intl.t('messenger.sales.visibility.set-audience-rules'),
        targetingSectionTitle: '',
      },
      showAudiencePreview: false,
      hideAddRule: false,
    };
  }

  constructor(owner: unknown, args: Args) {
    super(owner, args);
    this.activeBrandSetting = this.resolveInitialBrand();
    this.messengerSettingsService.customSaveHandler = this.saveConfig;
    registerDestructor(this, () => {
      this.messengerSettingsService.customSaveHandler = null;
      this.stopPollingGenerationStatus();
    });
    this.fetchConfig();
  }

  @action
  onLocaleChanged() {
    this.fetchConfig();
  }

  @action
  setBrandSetting(brandSetting: $TSFixMe) {
    this.activeBrandSetting = brandSetting;
    this.showAllSuggestions = false;
    this.updateStyleQueryParam(brandSetting);
    this.fetchConfig();
  }

  @action
  async fetchConfig() {
    if (!this.activeBrandSettingsId) {
      this.isLoading = false;
      return;
    }

    this.isLoading = true;

    try {
      let appId = this.appService.app.id;
      let response = (await get(
        `/ember/messenger_settings/sales/${appId}?brand_settings_id=${this.activeBrandSettingsId}&locale=${this.intl.primaryLocale}`,
      )) as SalesMessengerConfig;

      this.config = response;
      this.glowColor = response.glow_color;
      this.verticalPadding = response.vertical_padding ?? null;
      this.audiencePredicates = response.audience_predicates || [];
      this.smartSuggestionsEnabled = response.smart_suggestions_enabled ?? false;
      this.suggestions = response.suggestions ?? [];
      this.suggestionGroups = response.suggestion_groups ?? [];

      this.initAudienceRuleset(this.audiencePredicates);
      this.selectedPreset =
        this.audiencePredicates.length === 0 ? 'no-one' : this.detectPresetFromRuleset();
      this.generationJob = response.generation_job;
      this.hasUnsavedSuggestions = false;
      this.snapshotSavedState();

      // Set websiteUrl: live URL is the source of truth, fall back to job URL
      this.liveWebsiteUrl = response.last_live_website_url || '';
      this.websiteUrl = this.liveWebsiteUrl;
      let job = response.generation_job;
      if (job) {
        if (
          job.status === 'completed' &&
          job.draft_suggestion_groups &&
          job.draft_suggestion_groups.length > 0
        ) {
          // Generation completed but not yet saved — show drafts with unsaved banner
          this.suggestionGroups = job.draft_suggestion_groups;
          this.hasUnsavedSuggestions = true;
        }
        if (job.status === 'failed' && !response.last_live_website_url) {
          // First-time failure: show input with the attempted URL so the user can retry
          this.websiteUrl = job.website_url || '';
          this.generationJob = null;
        }
        if (job.status === 'pending' || job.status === 'scraping' || job.status === 'generating') {
          this.isGeneratingSuggestions = true;
          this.startPollingGenerationStatus();
        } else if (job.status === 'failed' && this.suggestionGroups.length === 0) {
          // Only surface stale errors on page load when there are no live suggestions
          this.failedGenerationUrl = job.website_url || '';
          this.failedGenerationErrorMessage = job.error_message || '';
          this.failedGenerationErrorCode = job.error_code || '';
          this.isGenerationErrorDismissed = false;
        }
      } else {
        this.failedGenerationUrl = '';
        this.failedGenerationErrorMessage = '';
        this.failedGenerationErrorCode = '';
        this.isGenerationErrorDismissed = false;
      }
    } catch (_e: unknown) {
      let defaultPredicates = SalesMessengerContainer.PRESET_PREDICATES['visitors-and-leads'];
      this.config = {
        brand_settings_id: this.activeBrandSettingsId,
        glow_color: null,
        vertical_padding: null,
        audience_predicates: defaultPredicates,
        smart_suggestions_enabled: false,
        suggestions: [],
        suggestion_groups: [],
        generation_job: null,
        last_live_website_url: null,
      };
      this.glowColor = null;
      this.verticalPadding = null;
      this.audiencePredicates = defaultPredicates;
      this.smartSuggestionsEnabled = false;
      this.suggestions = [];
      this.suggestionGroups = [];
      this.hasUnsavedSuggestions = false;
      this.selectedPreset = 'visitors-and-leads';
      this.initAudienceRuleset(defaultPredicates);
      this.snapshotSavedState();
    } finally {
      this.isLoading = false;
    }
  }

  @action
  selectPreset(preset: string) {
    this.selectedPreset = preset;
    if (preset !== 'specific') {
      let presetPredicates = SalesMessengerContainer.PRESET_PREDICATES[preset] || [];
      this.audiencePredicates = presetPredicates;
      this.initAudienceRuleset(presetPredicates);
    } else {
      // When switching to specific, init with empty ruleset — AudienceRulesEditor
      // auto-creates a default "Users" role predicate when supportsUserTargeting is true
      this.audiencePredicates = [];
      this.initAudienceRuleset([]);
    }
  }

  @action
  updateGlowColor(color: string) {
    this.glowColor = color;
  }

  @action
  updateVerticalPadding(event: Event) {
    let value = parseInt((event.target as HTMLInputElement).value, 10);
    if (isNaN(value)) {
      this.verticalPadding = null;
      return;
    }
    if (value < MIN_VERTICAL_PADDING) {
      value = MIN_VERTICAL_PADDING;
      this.notificationsService.notifyError(
        this.intl.t(
          'messenger.look-and-feel-settings.customize.launcher.visibility.position.padding-validation-error',
        ),
      );
    }
    this.verticalPadding = value;
  }

  @action
  setOpenSection(sectionId: string) {
    this.openSectionId = sectionId;
  }

  @action
  async saveConfig() {
    if (!this.activeBrandSettingsId) {
      return;
    }

    this.isSaving = true;

    try {
      let predicatesToSave = this.audiencePredicates;
      if (this.selectedPreset === 'specific' && this.audienceRuleset) {
        predicatesToSave = this.serializeAudienceRuleset();
        this.audiencePredicates = predicatesToSave;
      }

      let appId = this.appService.app.id;
      let response = (await put(
        `/ember/messenger_settings/sales/${appId}?locale=${this.intl.primaryLocale}`,
        {
          sales_messenger_configuration: {
            brand_settings_id: this.activeBrandSettingsId,
            glow_color: this.glowColor,
            vertical_padding: this.verticalPadding,
            audience_predicates: predicatesToSave,
            smart_suggestions_enabled: this.smartSuggestionsEnabled,
            active_suggestion_group_uuids: this.suggestionGroups.map((g) => g.group_uuid),
          },
        },
      )) as SalesMessengerConfig;

      this.config = response;
      this.glowColor = response.glow_color;
      this.verticalPadding = response.vertical_padding ?? null;
      this.audiencePredicates = response.audience_predicates || [];
      this.smartSuggestionsEnabled = response.smart_suggestions_enabled ?? false;
      this.suggestions = response.suggestions ?? [];
      this.suggestionGroups = response.suggestion_groups ?? [];

      this.initAudienceRuleset(this.audiencePredicates);
      this.selectedPreset =
        this.audiencePredicates.length === 0 ? 'no-one' : this.detectPresetFromRuleset();
      this.hasUnsavedSuggestions = false;
      this.snapshotSavedState();
      this.notificationsService.notifyConfirmation(this.intl.t('messenger.sales.save-success'));
    } catch (_e: unknown) {
      this.notificationsService.notifyError(this.intl.t('messenger.sales.save-error'));
    } finally {
      this.isSaving = false;
    }
  }

  @action
  toggleSmartSuggestions() {
    this.smartSuggestionsEnabled = !this.smartSuggestionsEnabled;
  }

  @action
  dismissGenerationError() {
    this.isGenerationErrorDismissed = true;
  }

  @action
  retryFailedGeneration() {
    this.generateSuggestions(this.failedGenerationUrl);
  }

  @action
  removeSuggestion(groupUuid: string) {
    this.suggestionGroups = this.suggestionGroups.filter((group) => group.group_uuid !== groupUuid);
  }

  @action
  toggleShowAllSuggestions() {
    this.showAllSuggestions = !this.showAllSuggestions;
  }

  @action
  updateWebsiteUrl(event: Event) {
    this.websiteUrl = (event.target as HTMLInputElement).value;
  }

  @action
  startEditingWebsiteUrl() {
    this.isEditingWebsiteUrl = true;
  }

  @action
  cancelEditingWebsiteUrl() {
    this.isEditingWebsiteUrl = false;
    // Restore URL from the last known job
    this.websiteUrl = this.generationJob?.website_url || '';
  }

  @action
  async generateSuggestions(overrideUrl?: string) {
    // Guard: Ember may pass an event object when called from a template action
    let rawUrl = typeof overrideUrl === 'string' ? overrideUrl : this.websiteUrl;
    if (!rawUrl.trim()) {
      return;
    }

    // Clear any previous error state
    this.failedGenerationUrl = '';
    this.failedGenerationErrorMessage = '';
    this.failedGenerationErrorCode = '';
    this.isGenerationErrorDismissed = false;

    let url = rawUrl.trim();
    if (!/^https?:\/\//i.test(url)) {
      url = `https://${url}`;
    }
    this.websiteUrl = url;

    // Snapshot current state so we can revert on failure
    this.previousSuggestions = [...this.suggestionGroups];
    this.previousWebsiteUrl = this.liveWebsiteUrl;
    this.previousGenerationJob = this.generationJob ? { ...this.generationJob } : null;

    // Show generating state immediately so the UI displays the new URL
    this.isGeneratingSuggestions = true;
    this.isEditingWebsiteUrl = false;

    try {
      let appId = this.appService.app.id;
      await post(`/ember/messenger_settings/sales/${appId}/generate_suggestions`, {
        sales_messenger_configuration: {
          website_url: url,
          brand_settings_id: this.activeBrandSettingsId,
        },
      });

      this.startPollingGenerationStatus();
    } catch (e: unknown) {
      this.isGeneratingSuggestions = false;
      if (e instanceof Error && 'status' in e && (e as { status: number }).status === 409) {
        this.notificationsService.notifyError(
          this.intl.t('messenger.sales.smart-suggestions.generate-in-progress'),
        );
      } else {
        this.notificationsService.notifyError(
          this.intl.t('messenger.sales.smart-suggestions.generate-error'),
        );
      }
    }
  }

  private startPollingGenerationStatus() {
    this.stopPollingGenerationStatus();
    this.generationPollTimer = setTimeout(() => this.pollGenerationStatus(), 5000);
  }

  private stopPollingGenerationStatus() {
    if (this.generationPollTimer) {
      clearTimeout(this.generationPollTimer);
      this.generationPollTimer = null;
    }
  }

  private async pollGenerationStatus() {
    try {
      let appId = this.appService.app.id;
      let response = (await get(
        `/ember/messenger_settings/sales/${appId}/generation_status?brand_settings_id=${this.activeBrandSettingsId}`,
      )) as GenerationJob;

      if (response.status === 'completed') {
        this.isGeneratingSuggestions = false;
        this.stopPollingGenerationStatus();
        if (response.draft_suggestion_groups) {
          // New candidates from generation — show them as the current list
          this.suggestionGroups = response.draft_suggestion_groups;
        } else {
          await this.fetchConfig();
        }
        // Generation just finished in this session — remind the user to save,
        // regardless of whether the backend returned drafts or auto-promoted.
        this.hasUnsavedSuggestions = true;
      } else if (response.status === 'failed') {
        this.isGeneratingSuggestions = false;
        this.stopPollingGenerationStatus();
        let failedUrl = this.websiteUrl || response.website_url;
        let errorMessage = response.error_message || '';
        let errorCode = response.error_code || '';

        if (this.previousSuggestions.length > 0) {
          this.suggestionGroups = this.previousSuggestions;
          this.generationJob = this.previousGenerationJob;
          this.websiteUrl = this.previousWebsiteUrl;
        } else {
          // First-time failure: clear generationJob so showUrlInput becomes true.
          // websiteUrl already has the failed URL from generateSuggestions().
          this.generationJob = null;
        }

        // Set error state AFTER fetchConfig so it isn't cleared
        this.failedGenerationUrl = failedUrl;
        this.failedGenerationErrorMessage = errorMessage;
        this.failedGenerationErrorCode = errorCode;
        this.isGenerationErrorDismissed = false;
      } else {
        this.startPollingGenerationStatus();
      }
    } catch (_e: unknown) {
      this.isGeneratingSuggestions = false;
      this.stopPollingGenerationStatus();
    }
  }

  private resolveInitialBrand(): $TSFixMe {
    let styleParam = (this.router.currentRoute?.queryParams as Record<string, string>)?.style;
    if (styleParam) {
      let match = this.brandSettings.findBy('id', styleParam);
      if (match) {
        return match;
      }
    }
    return this.lookAndFeel.defaultBrand;
  }

  private updateStyleQueryParam(brandSetting: $TSFixMe) {
    let isDefault = brandSetting.isDefault;
    (this.router as $TSFixMe).replaceWith(this.router.currentRouteName, {
      queryParams: { style: isDefault ? null : brandSetting.id },
    });
  }

  private static USER_TYPE_TO_PRESET: Record<string, string> = {
    non_user: 'visitors-and-leads',
    everyone: 'everyone',
  };

  private detectPresetFromRuleset(): string {
    if (!this.audienceRuleset) {
      return 'no-one';
    }
    let roleGroup = this.audienceRuleset.get('rolePredicateGroup');
    let predicateGroup = this.audienceRuleset.get('predicateGroup');
    let hasCustomPredicates = predicateGroup?.get('hasPredicates');
    if (hasCustomPredicates) {
      return 'specific';
    }
    let userType = roleGroup?.get('userPredicate.userType');
    if (!userType) {
      return 'no-one';
    }
    return SalesMessengerContainer.USER_TYPE_TO_PRESET[userType] || 'specific';
  }

  private initAudienceRuleset(rawPredicates: Array<Record<string, unknown>>) {
    let allFragments = PredicateGroup.convertRawPredicates(this.store, rawPredicates);

    let roleFragments = allFragments.filter((f: $TSFixMe) => f.isUserPredicate);
    let otherFragments = allFragments.filter((f: $TSFixMe) => !f.isUserPredicate);

    if (roleFragments.length === 0) {
      let defaultRole = PredicateGroup.convertRawPredicates(this.store, [
        { attribute: 'role', type: 'role', comparison: 'in', value: ['user_role'] },
      ]);
      roleFragments = defaultRole;
    }

    let predicateGroup = this.store.createFragment('predicates/predicate-group', {
      predicates: otherFragments,
    });

    let rolePredicateGroup = this.store.createFragment('predicates/predicate-group', {
      predicates: roleFragments,
    });

    let clientPredicateGroup = this.store.createFragment('predicates/predicate-group', {
      predicates: [],
    });

    let defaultPredicateGroup = this.store.createFragment('predicates/predicate-group', {
      predicates: [],
    });

    this.audienceRuleset = EmberObject.create({
      predicateGroup,
      rolePredicateGroup,
      clientPredicateGroup,
      defaultPredicateGroup,
      fetchAudiencePreviewTask: EmberObject.create({
        perform: () => Promise.resolve({ count: 0 }),
        isRunning: false,
      }),
    });
  }

  private serializeAudienceRuleset(): Array<Record<string, unknown>> {
    if (!this.audienceRuleset) {
      return [];
    }
    let serialize = (p: $TSFixMe) => (p.serialize ? p.serialize() : p);

    // Use Ember's get() to read values set via Ember's set() by AudienceRulesEditor
    let rolePredicates = (this.audienceRuleset.get('rolePredicateGroup.predicates') || []).map(
      serialize,
    );
    let otherPredicates = (this.audienceRuleset.get('predicateGroup.predicates') || []).map(
      serialize,
    );

    return [...rolePredicates, ...otherPredicates];
  }

  private snapshotSavedState() {
    this.savedGlowColor = this.glowColor;
    this.savedVerticalPadding = this.verticalPadding;
    this.savedPredicates = JSON.stringify(this.audiencePredicates);
    this.savedPreset = this.selectedPreset;
    this.savedSmartSuggestionsEnabled = this.smartSuggestionsEnabled;
  }

  private getTargetingAttributes(): {
    personAttrs: $TSFixMe[];
    companyAttrs: $TSFixMe[];
  } {
    let userAttrs = this.attributeService.filterableUserAttributesForMessaging || [];
    let visitorAttrs = this.attributeService.visitorMessagingAttributes || [];
    let companyAttrs = this.attributeService.filterableCompanyAttributesForMessaging || [];
    return {
      personAttrs: [...userAttrs, ...visitorAttrs],
      companyAttrs,
    };
  }

  private getAttributeIdentifiers(): string[] {
    let { personAttrs, companyAttrs } = this.getTargetingAttributes();
    let validAttributes = this.appService.app?.companiesActive
      ? [...personAttrs, ...companyAttrs]
      : personAttrs;
    let identifiers = validAttributes
      .filter((attr: $TSFixMe) => attr?.identifier)
      .map((attr: $TSFixMe) => attr.identifier)
      .flatMap((identifier: string) =>
        identifier.startsWith('user_event_summaries.')
          ? [`${identifier}.count`, `${identifier}.first`, `${identifier}.last`]
          : identifier,
      );
    return [...new Set([...identifiers, 'role'])];
  }

  private getAttributeGroups(): Array<{ heading: string; attributes: $TSFixMe[] }> {
    let { personAttrs, companyAttrs } = this.getTargetingAttributes();
    let groups = [
      {
        heading: this.intl.t('operator.workflows.visual-builder.workflow-audience.person-data'),
        attributes: personAttrs,
      },
    ];
    if (this.appService.app?.companiesActive) {
      groups.push({
        heading: this.intl.t('operator.workflows.visual-builder.workflow-audience.company-data'),
        attributes: companyAttrs,
      });
    }
    return groups;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'MessengerSettings::Sales::Container': typeof SalesMessengerContainer;
  }
}
